import {CommonModule} from '@angular/common';
import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {authGuard} from './guards/auth.guard';
import {AdminComponent} from './layout/admin/admin.component';
import {LockScreenComponent} from './pages/authentication/lock-screen/lock-screen.component';
import {LoginComponent} from './pages/authentication/login/login.component';
import {MyFeesComponent} from './pages/my-fees/my-fees.component';
import {OnboardingKycComponent} from './pages/authentication/onboarding-kyc/onboarding-kyc.component';
import {OnboardingComponent} from './pages/authentication/onboarding/onboarding.component';
import {OnboardingSuccessComponent} from './pages/authentication/onboarding-success/onboarding-success.component';
import {PasswordResetRequestComponent} from './pages/authentication/password-reset-request/password-reset-request.component';
import {PasswordResetComponent} from './pages/authentication/password-reset/password-reset.component';
import {SignupComponent} from './pages/authentication/signup/signup.component';
import {DocumentationComponent} from './pages/documentation/documentation.component';
import {GateweeErrorPageComponent} from '@gatewee/components/v2';
import {HomeComponent} from './pages/home/home.component';
import {RecipientKeysComponent} from './pages/recipient-keys/recipient-keys.component';
import {RecipientPixKeysComponent} from './pages/recipient-pix-keys/recipient-pix-keys.component';
import {UsersComponent} from './pages/users/users.component';
import {WhatsappSettingsComponent} from './pages/whatsapp-settings/whatsapp-settings.component';
import {AssumeRoleComponent} from './pages/assume-role/assume-role.component';
const appRoutes: Routes = [
  // Authentication
  {
    path: '',

    redirectTo: '/login',
    pathMatch: 'full',
  },
  {
    path: '',
    canActivate: [],
    children: [
      {path: 'login', component: LoginComponent},
      {path: 'signup/:token', component: SignupComponent},
      {path: 'password-reset/:token', component: PasswordResetComponent},
      {
        path: 'password-reset-request',
        component: PasswordResetRequestComponent,
      },
      {path: 'onboarding', component: OnboardingComponent},
      {path: 'onboarding/kyc/:identifier', component: OnboardingKycComponent},
      {path: 'onboarding/:identifier', component: OnboardingComponent},
      {path: 'onboarding-success', component: OnboardingSuccessComponent},
    ],
  },
  {path: 'documentation', component: DocumentationComponent},
  {path: 'assume-role', component: AssumeRoleComponent},

  // Admin
  {
    path: 'panel',
    component: AdminComponent,
    canActivate: [authGuard],
    data: {breadcrumb: 'nav.breadcrumb.home'},
    children: [
      {path: '', component: HomeComponent},
      {
        path: 'transactions',
        data: {breadcrumb: 'nav.menu.transactions'},
        loadChildren: () =>
          import('./pages/transactions/transactions.module').then(
            m => m.TransactionsModule,
          ),
      },
      {
        path: 'checkouts',
        data: {breadcrumb: 'nav.menu.checkouts'},
        loadChildren: () =>
          import('./pages/checkouts/checkouts.module').then(
            m => m.CheckoutsModule,
          ),
      },

      {path: 'recipients/keys', component: RecipientKeysComponent},
      {path: 'recipients/pix-keys', component: RecipientPixKeysComponent},
      {
        path: 'invoices',
        data: {breadcrumb: 'nav.menu.invoices'},
        loadChildren: () =>
          import('./pages/invoices/invoices.module').then(
            m => m.InvoicesModule,
          ),
      },
      {
        path: 'refunds',
        data: {breadcrumb: 'nav.menu.refunds'},
        loadChildren: () =>
          import('./pages/refunds/refunds.module').then(m => m.RefundsModule),
      },
      {
        path: 'clients',
        data: {breadcrumb: 'nav.menu.clients'},
        loadChildren: () =>
          import('./pages/clients/clients.module').then(m => m.ClientsModule),
      },
      {
        path: 'withdrawals',
        data: {breadcrumb: 'nav.menu.withdrawals'},
        loadChildren: () =>
          import('./pages/withdrawals/withdrawals.module').then(
            m => m.WithdrawalsModule,
          ),
      },
      {
        path: 'subscriptions',
        data: {breadcrumb: 'nav.menu.subscriptions'},
        loadChildren: () =>
          import('./pages/subscriptions/subscriptions.module').then(
            m => m.SubscriptionsModule,
          ),
      },
      {
        path: 'subscriptions-plans',
        data: {breadcrumb: 'nav.menu.subscriptionsPlans'},
        loadChildren: () =>
          import('./pages/subscriptions-plans/subscriptions-plans.module').then(
            m => m.SubscriptionsPlansModule,
          ),
      },
      {
        path: 'subscriptions-plans-v2',
        redirectTo: 'subscriptions-plans/new',
        pathMatch: 'full',
      },
      {
        path: 'subscriptions-plans-v2/:id',
        redirectTo: 'subscriptions-plans/edit/:id',
        pathMatch: 'full',
      },
      {
        path: 'chargebacks',
        data: {breadcrumb: 'nav.menu.chargebacks'},
        loadChildren: () =>
          import('./pages/chargebacks/chargebacks.module').then(
            m => m.ChargebacksModule,
          ),
      },
      {
        path: 'support-tickets',
        data: {breadcrumb: 'nav.menu.supportTickets'},
        loadChildren: () =>
          import('./pages/support-tickets/support-tickets.module').then(
            m => m.SupportTicketsModule,
          ),
      },
      {
        path: 'tasks',
        data: {breadcrumb: 'features.tasks.title'},
        loadChildren: () =>
          import('./pages/tasks/tasks.module').then(m => m.TasksModule),
      },
      {
        path: 'reports',
        data: {breadcrumb: 'features.reports.title'},
        loadChildren: () =>
          import('./pages/reports/reports.module').then(m => m.ReportsModule),
      },
      {
        path: 'notifications',
        data: {breadcrumb: 'features.notifications.title'},
        loadChildren: () =>
          import('./pages/notifications/notifications.module').then(
            m => m.NotificationsModule,
          ),
      },
      {
        path: 'profile',
        data: {breadcrumb: 'nav.menu.profile'},
        loadChildren: () =>
          import('./pages/profile/profile.module').then(m => m.ProfileModule),
      },
      {path: 'settings/whatsapp', component: WhatsappSettingsComponent},
      {
        path: 'my-fees',
        component: MyFeesComponent,
        data: {breadcrumb: 'nav.menu.myFees'},
      },
      {
        path: 'settings/bank-accounts',
        loadChildren: () =>
          import('./pages/bank-accounts/bank-accounts.module').then(
            m => m.BankAccountsModule,
          ),
      },
      {path: 'settings/users', component: UsersComponent},
    ],
  },

  // Errors
  {
    path: 'page-error-400',
    component: GateweeErrorPageComponent,
    data: {code: '400'},
  },
  {
    path: 'page-error-403',
    component: GateweeErrorPageComponent,
    data: {code: '403'},
  },
  {
    path: 'page-error-404',
    component: GateweeErrorPageComponent,
    data: {code: '404'},
  },
  {
    path: 'page-error-500',
    component: GateweeErrorPageComponent,
    data: {code: '500'},
  },
  {
    path: 'page-error-503',
    component: GateweeErrorPageComponent,
    data: {code: '503'},
  },
  {path: 'page-lock-screen', component: LockScreenComponent},

  // Default
  {path: '**', component: GateweeErrorPageComponent, data: {code: '404'}},
];

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: 'top',
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
